import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  AuditInputType,
  AuditSortField,
  AuditStatus,
  SortOrder,
} from "@/types";

export type HistoryFiltersFilters = {
  status: AuditStatus | "";
  inputType: AuditInputType | "";
  dateFrom: string;
  dateTo: string;
  hasActive: boolean;
  onStatusChange: (value: AuditStatus | "") => void;
  onInputTypeChange: (value: AuditInputType | "") => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onClear: () => void;
};

export type HistoryFiltersSort = {
  by: AuditSortField;
  order: SortOrder;
  onByChange: (value: AuditSortField) => void;
  onOrderToggle: () => void;
};

type HistoryFiltersProps = {
  filters: HistoryFiltersFilters;
  sort: HistoryFiltersSort;
};

export function HistoryFilters({ filters, sort }: HistoryFiltersProps) {
  const { t } = useTranslation(["history"]);

  const statusOptions: Array<{ value: AuditStatus | ""; label: string }> = [
    { value: "", label: t("history:filters.allStatuses") },
    { value: "COMPLETED", label: t("history:filters.completed") },
    { value: "PROCESSING", label: t("history:filters.processing") },
    { value: "QUEUED", label: t("history:filters.queued") },
    { value: "FAILED", label: t("history:filters.failed") },
  ];

  const inputTypeOptions: Array<{ value: AuditInputType | ""; label: string }> = [
    { value: "", label: t("history:filters.allTypes") },
    { value: "website", label: t("history:filters.website") },
    { value: "document", label: t("history:filters.document") },
  ];

  const sortFieldOptions: Array<{ value: AuditSortField; label: string }> = [
    { value: "createdAt", label: t("history:filters.date") },
    { value: "documentName", label: t("history:filters.documentName") },
    { value: "riskLevel", label: t("history:filters.riskLevelSort") },
  ];

  return (
    <div className="mb-4 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-4 shadow-[var(--sh-xs)]">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--foreground)]">
            {t("history:filters.status")}
          </label>
          <Select
            items={statusOptions}
            value={filters.status}
            onValueChange={(value) => {
              if (value === null) return;
              filters.onStatusChange(value);
            }}
          >
            <SelectTrigger id="history-filter-status" className="w-full">
              <SelectValue
                placeholder={t("history:filters.allStatuses")}
              />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--foreground)]">
            {t("history:filters.type")}
          </label>
          <Select
            items={inputTypeOptions}
            value={filters.inputType}
            onValueChange={(value) => {
              if (value === null) return;
              filters.onInputTypeChange(value);
            }}
          >
            <SelectTrigger
              id="history-filter-type"
              data-test="history-filter-type"
              className="w-full"
            >
              <SelectValue placeholder={t("history:filters.allTypes")} />
            </SelectTrigger>
            <SelectContent>
              {inputTypeOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--foreground)]">
            {t("history:filters.fromDate")}
          </label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => filters.onDateFromChange(e.target.value)}
            className="w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--foreground)]">
            {t("history:filters.toDate")}
          </label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => filters.onDateToChange(e.target.value)}
            className="w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--foreground)]">
            {t("history:filters.sortBy")}
          </label>
          <div className="flex gap-2">
            <Select
              items={sortFieldOptions}
              value={sort.by}
              onValueChange={(value) => {
                if (value === null) return;
                sort.onByChange(value);
              }}
            >
              <SelectTrigger id="history-filter-sort-by" className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortFieldOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={sort.onOrderToggle}
              data-test="history-sort-order"
              title={sort.order === "asc"
                ? t("history:filters.ascending")
                : t("history:filters.descending")}
              aria-label={sort.order === "asc"
                ? t("history:filters.ascending")
                : t("history:filters.descending")}
            >
              {sort.order === "asc" ? "↑" : "↓"}
            </Button>
          </div>
        </div>
      </div>

      {filters.hasActive && (
        <div className="mt-3 border-t border-[var(--border)] pt-3">
          <Button
            variant="link"
            size="sm"
            onClick={filters.onClear}
            data-test="history-clear-filters"
            className="px-0 text-[var(--primary)]"
          >
            {t("history:filters.clearAll")}
          </Button>
        </div>
      )}
    </div>
  );
}
